'use client';

import { useId, useState, type ReactNode } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import type { PageFilterField } from 'elowen-plugin-ui-kit';

import { interpolate, useTranslation } from '../../lib/i18n';
import { useMobile } from '../../lib/useMobile';
import { Button } from './Button';
import { HelpTip } from './HelpTip';
import { Modal, ModalBody } from './Modal';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';

/** The ONE condensed filter control of the canonical page toolbar.
 *
 *  WHAT IT IS NOT: a search box. A register's text search is a permanent control that narrows rows as
 *  you type; it lives in the toolbar's own `search` slot and never appears in here or as a chip. Folding
 *  it in would put the page's most-used control two clicks away and give it a chip that cannot be read
 *  at a glance.
 *
 *  IT OWNS ALMOST NOTHING, and that is the design. It owns the trigger, whether the panel is open, and
 *  the arithmetic of how many filters are active. It does NOT own what a filter IS. A page hands it a
 *  rendered `control` and says whether that control is currently filtering; the control can be a
 *  Segmented, a Toggle, `DateRangeFilter`, `ProjectFilterPills`, a pair of number inputs, or anything a
 *  page grows next.
 *
 *  The first version of this component took `options` + `value` + `onChange` and decided "active" by
 *  comparing the value to a neutral option. That is a single-select picker wearing a filter's name, and
 *  every real filter in the app already fails it: Memory's grouping and show-category filters are
 *  booleans with no option list, `DateRangeFilter` is a preset plus two dates, `ProjectFilterPills` is a
 *  project catalog adapter over live data. Each of those would have had to be flattened into a synthetic option list to
 *  pass through, and the flattening — not the filter — would then decide whether the page was filtered.
 *
 *  So the page states it. `active` is a fact only the page can know, `activeLabel` is the wording only
 *  the page can write, and `onReset` is the undo only the page can perform. The published union makes the
 *  three inseparable: an active field that cannot say what it is doing, or cannot be switched off, does
 *  not typecheck. */
type ActivePageFilterField = Extract<PageFilterField, { active: true }>;

/** The active fields, as a narrowed type so the chip code reaches `activeLabel` and `onReset` without
 *  an optional chain that would quietly render a chip nobody can dismiss.
 *
 *  Note what it CANNOT do: it has no access to any control's value — a control is an opaque node here —
 *  so "is this page filtered" is answered by the page's own flag and by nothing else. */
function activeFieldsOf(fields: PageFilterField[]): ActivePageFilterField[] {
  return fields.filter((field): field is ActivePageFilterField => field.active);
}

/** The shared label shell every filter control sits in, so a Toggle, a Segmented and a date range all
 *  read as the same kind of thing in one panel.
 *
 *  It is deliberately NOT `components/ui/Field.tsx`. That field wraps its control in a `<label>`, which
 *  is right for one input and wrong for most filters: a wrapping label binds to the first labelable
 *  descendant, so a radio group would have the field's name attached to its first radio and a two-input
 *  date range to whichever input came first. A labelled `group` names the whole control, whatever the
 *  control turns out to be. */
function PageFilterFieldShell({ field }: { field: PageFilterField }) {
  const labelId = useId();
  return (
    <div className="page-filters__field" role="group" aria-labelledby={labelId}>
      <span className="page-filters__field-label">
        <span id={labelId}>{field.label}</span>
        {field.hint ? <HelpTip align="left">{field.hint}</HelpTip> : null}
      </span>
      <div className="page-filters__field-control">{field.control}</div>
    </div>
  );
}

function FilterFields({ fields, actions, onAction }: { fields: PageFilterField[]; actions?: ReactNode; onAction?: () => void }) {
  return (
    <>
      {fields.length > 0 ? (
        <div className="page-filters__fields">
          {fields.map((field) => <PageFilterFieldShell key={field.id} field={field} />)}
        </div>
      ) : null}
      {actions != null ? (
        <div
          className="page-filters__actions"
          data-testid="page-filters-actions"
          onClick={(event) => { if (onAction && (event.target as Element).closest('button, a')) onAction(); }}
        >
          {actions}
        </div>
      ) : null}
    </>
  );
}

/** The trigger and the panel behind it. Renders NOTHING when there is neither a filter nor an action: a
 *  page with nothing to offer must not carry a control that opens an empty surface.
 *
 *  `actions` are buttons that act on the page's data rather than narrow it (a destructive "delete all"
 *  is the one that exists). They sit under the fields, after a rule, and the page owns what they do and
 *  any confirmation they need. A page with actions and no fields shows the actions alone: the rule only
 *  separates them from fields that are there. A panel that holds them is no longer only "Filters", so the trigger and
 *  the sheet are then named "Options"; a page with filters alone keeps the plainer word. `named` states the
 *  word outright, for the sibling pages of one deck that should open the same-looking control whether or not
 *  each has an action. */
export function PageFilters({ fields, actions, iconOnly = false, named = actions != null ? 'options' : 'filters' }: {
  fields: PageFilterField[]; actions?: ReactNode; named?: 'filters' | 'options' | 'actions';
  /** Host-only compact trigger for crowded mobile toolbars; keeps the full accessible name. */
  iconOnly?: boolean;
}) {
  const { t } = useTranslation();
  const phone = useMobile();
  const [open, setOpen] = useState(false);
  const activeCount = activeFieldsOf(fields).length;

  if (fields.length === 0 && actions == null) return null;

  const title = named === 'actions' ? t.common.actions : named === 'options' ? t.common.filterOptions : t.common.filters;
  const label = (
    <>
      {title}
      {activeCount > 0 ? <span className="page-filters__count" aria-hidden>{activeCount}</span> : null}
    </>
  );
  // The badge beside the label is decoration; without an accessible name the control announces "Filters 2".
  const accessibleName = named === 'actions' ? title : activeCount > 0
    ? interpolate(named === 'options' ? t.common.filterOptionsWithCount : t.common.filtersWithCount, { count: activeCount })
    : undefined;

  // A phone gets the app's own dialog as a bottom sheet rather than a popover anchored to a control that
  // is a third of the screen wide. Both presentations are the shared primitives: Radix owns the popover's
  // dismissal and focus return, `Modal` owns the sheet's trap, Escape and focus restoration. The trigger
  // is rendered once per branch rather than hoisted, because `asChild` COMPOSES handlers — an `onClick`
  // written for the sheet would still fire beside Radix's own toggle and close the popover as it opened.
  if (phone) {
    return (
      <>
        <Button
          type="button"
          icon={SlidersHorizontal}
          className="page-filters__trigger"
          data-testid="page-filters-trigger"
          aria-label={accessibleName ?? (iconOnly ? title : undefined)}
          title={iconOnly ? title : undefined}
          size={iconOnly ? 'icon' : 'default'}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          {iconOnly ? null : label}
        </Button>
        {open ? (
          <Modal title={title} presentation="sheet" size="md" icon={SlidersHorizontal} onClose={() => setOpen(false)}>
            {/* An action may open a window of its own (the Users defaults overlay). The sheet is a modal
                above the page band, so it would stay on top of that window; it steps aside instead. */}
            <ModalBody><FilterFields fields={fields} actions={actions} onAction={() => setOpen(false)} /></ModalBody>
          </Modal>
        ) : null}
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          icon={SlidersHorizontal}
          className="page-filters__trigger"
          data-testid="page-filters-trigger"
          aria-label={accessibleName ?? (iconOnly ? title : undefined)}
          title={iconOnly ? title : undefined}
          size={iconOnly ? 'icon' : 'default'}
        >
          {iconOnly ? null : label}
        </Button>
      </PopoverTrigger>
      {/* Radix gives the content `role="dialog"`; it has no visible heading, so it names itself. */}
      <PopoverContent align="start" aria-label={title} className="page-filters__panel">
        <FilterFields fields={fields} actions={actions} />
      </PopoverContent>
    </Popover>
  );
}

/** The active-filter chips, one line below the toolbar row. Each chip carries its field's own wording
 *  and its own reset; past one active filter the group also offers a single reset for all of them.
 *  Nothing renders while nothing is filtering.
 *
 *  The whole row is ONE group on the shared default step: a chip is the reset of its filter, so it is a
 *  button (never a caption with its own padding-derived height) and the clear-all beside it is the same
 *  step. This is a filter bar under the page toolbar, not a compact action group inside a card or a list
 *  row, so it takes the default step and its coarse pointer floor from the button scale. The chip's own
 *  class keeps only what a filter name needs — a bound and an ellipsis. */
export function PageFilterChips({ fields }: { fields: PageFilterField[] }) {
  const { t } = useTranslation();
  const active = activeFieldsOf(fields);
  if (active.length === 0) return null;

  return (
    <div className="page-filters__chips" role="group" aria-label={t.common.filtersActive} data-testid="page-filter-chips">
      {active.map((field) => (
        <Button
          key={field.id}
          type="button"
          className="page-filters__chip"
          aria-label={interpolate(t.common.filterRemove, { name: field.activeLabel })}
          onClick={field.onReset}
        >
          <span className="page-filters__chip-name">{field.activeLabel}</span>
          <X size={12} aria-hidden />
        </Button>
      ))}
      {active.length > 1 ? (
        <Button variant="ghost" className="page-filters__clear" onClick={() => { for (const field of active) field.onReset(); }}>
          {t.common.filtersClear}
        </Button>
      ) : null}
    </div>
  );
}
